import { periodSuffix, getDateRangeForPeriod, shouldFetchFromFirestore } from '@/utils/firestoreUtils'
import {
    projectMap,
    priorityMap,
    calculateProjectsWorkSummaryForAllUsers,
    getMergedTimeEntriesForUsers,
    triggerSnackbar,
} from '@/utils/utils'
import { isUserActive } from '@/utils/projectUtils'

/**
 * Mixin for shared functionality between AdminDashboard and TeamLeadDashboard components.
 * Provides data, computed properties, watchers, and methods for managing projects report data,
 * project priorities, permissions, and UI options.
 * 
 * Expected Vuex getters (must be mapped in components using this mixin):
 * - projects: "getManagedProjects"
 * - users: "getUsers"
 * - getRights: "getRights"
 * - getCompanies: "getCompanies"
 */
export const adminAndTeamLeadDashboardMixin = {
    data() {
        return {
            selectedPeriod: 'today',
            projectsReportData: [],
            /**
             * Loading state for Firestore data fetching.
             * @type {Boolean}
             * @default false
             */
            isLoadingFirestore: false,
            /**
             * Snackbar configuration for displaying notifications.
             * @type {Object}
             */
            snackbar: {
                show: false,
                message: '',
                color: '',
                timeout: 5000,
            },
        }
    },

    computed: {
        /**
         * Returns the period suffix label for display.
         * @returns {string} The period suffix label.
         */
        periodSuffixLabel() {
            return periodSuffix(this.selectedPeriod)
        },

        /**
         * Returns a map of project IDs to project names.
         * @returns {Object.<string, string>} Map of projectId to project name.
         */
        doProjectMap() {
            return projectMap(this.projects)
        },

        /**
         * Returns a map of project IDs to priorities.
         * @returns {Object.<string, string>} Map of projectId to priority.
         */
        doPriorityMap() {
            return priorityMap(this.projects)
        },

        /**
         * Checks if the user has permission to add new projects.
         * @returns {boolean} `true` if the user can add new projects, `false` otherwise.
         */
        canAddNewProject() {
            return this.getRights?.addNewProject || false
        },

        /**
         * Checks if the user has permission to reset all project priorities.
         * @returns {boolean} `true` if the user can reset priorities, `false` otherwise.
         */
        canResetPriorities() {
            return this.getRights?.resetAllProjectPriorities || false
        },

        /**
         * Generates an array of team leader options based on the list of users.
         * - Each option includes the user's display name (name + surname or email) and UID.
         * @returns {Array<Object>} Array of objects with `title` (display name) and `value` (UID).
         */
        teamLeaderOptions() {
            return (this.users || [])
                .filter((user) => isUserActive(user))
                .map((user) => {
                    const displayName =
                        user.name && user.surname
                            ? `${user.name} ${user.surname}`
                            : user.email || 'Unknown User'

                    return {
                        title: displayName,
                        value: user.uid,
                    }
                })
        },

        /**
         * Retrieves client options based on the companies fetched from Vuex.
         * @returns {Array<string>} Array of client titles (company names).
         */
        clientOptions() {
            const options = (this.getCompanies || []).map(
                (company) => company.title
            ) // Extract titles as strings
            return options
        },
    },

    watch: {
        /**
         * Watches for changes to the `tab` query parameter in the route.
         * Updates the `tab` data property when the query parameter changes.
         * @param {string} newTab - The new value of the `tab` query parameter.
         */
        '$route.query.tab': {
            immediate: true,
            handler(newTab) {
                if (newTab) {
                    this.tab = newTab
                }
            },
        },

        /**
         * Watches for tab changes and refreshes data when switching to Projects tab.
         * @param {string} newTab - The new tab value.
         * @param {string} oldTab - The previous tab value.
         */
        tab(newTab, oldTab) {
            // Only refresh if switching TO projects tab (not on initial mount when oldTab is undefined)
            if (newTab === 'projects' && oldTab !== undefined && oldTab !== 'projects') {
                // Refresh data when switching to Projects tab to ensure it's up to date
                this.updateProjectsReportData(this.selectedPeriod)
            }
        },
    },

    methods: {
        /**
         * Handles period change from the ProjectsPeriodFilterMenu component.
         * @param {string} newPeriod - The new period identifier.
         * @param {Object|null} customDateRange - Optional custom date range object.
         */
        async handlePeriodChange(newPeriod, customDateRange = null) {
            this.selectedPeriod = newPeriod
            await this.updateProjectsReportData(newPeriod, customDateRange)
        },

        /**
         * Updates the projects report data for the given period.
         * Fetches merged time entries (RTDB + Firestore) and calculates project summaries.
         * @param {string} periodKey - Period identifier (e.g., 'today', 'yesterday', 'thisWeek', etc.).
         * @param {Object|null} customDateRange - Optional custom date range object.
         */
        async updateProjectsReportData(periodKey, customDateRange = null) {
            const dateRange = getDateRangeForPeriod(periodKey, customDateRange)
            const { startDate: start, endDate: end } = dateRange

            // Check if Firestore fetch is needed
            const needsFirestore = shouldFetchFromFirestore(periodKey, customDateRange)

            try {
                if (needsFirestore) {
                    this.isLoadingFirestore = true
                }

                // Get merged time entries (RTDB + Firestore if needed)
                const mergedTimeEntriesByUser = await getMergedTimeEntriesForUsers(
                    this.users || [],
                    periodKey,
                    customDateRange
                )

                // Calculate report data using merged entries
                this.projectsReportData = calculateProjectsWorkSummaryForAllUsers(
                    this.users || [],
                    this.projects || [],
                    {
                        dateRange: {
                            startDate: start,
                            endDate: end,
                        },
                        excludeEmpty: false,
                        mergedTimeEntriesByUser, // Pass merged entries
                    }
                )
            } finally {
                this.isLoadingFirestore = false
            }
        },

        /**
         * Confirms and resets the priorities of all managed projects to "Normal."
         * - Fetches the IDs of all managed projects and dispatches a Vuex action to reset their priorities.
         * - Displays a success or error snackbar based on the result.
         *
         * @async
         * @function confirmResetProjectsPriority
         * @returns {Promise<void>}
         * @throws {Error} Logs an error to the console and shows an error snackbar if the reset operation fails.
         */
        async confirmResetProjectsPriority() {
            try {
                const managedProjectIds = this.projects.map(
                    (project) => project.projectId
                )

                if (managedProjectIds.length === 0) {
                    triggerSnackbar(
                        this.snackbar,
                        'You have no managed projects to reset.',
                        'info'
                    )
                    return
                }

                // Dispatch a Vuex action to reset priorities of managed projects
                await this.$store.dispatch(
                    'resetPrioritiesOfProjects',
                    managedProjectIds
                )

                triggerSnackbar(
                    this.snackbar,
                    'Priorities of all projects have been reset to "Normal".',
                    'success'
                )
            } catch (error) {
                console.error('Failed to reset priorities:', error)
                triggerSnackbar(
                    this.snackbar,
                    'Failed to reset priorities.',
                    'error'
                )
            }
        },

        /**
         * Resets project priorities for all users to "Normal."
         * - Dispatches a Vuex action to reset all users' project priorities.
         * - Displays a success snackbar upon completion.
         *
         * @async
         * @function resetProjectPrioritiesForAllUsers
         * @returns {Promise<void>}
         * @throws {Error} Logs an error to the console if the reset operation fails.
         */
        async resetProjectPrioritiesForAllUsers() {
            try {
                await this.$store.dispatch('resetAllUsersProjectPriorities')
                triggerSnackbar(
                    this.snackbar,
                    'All users priorities have been reset to "Normal".',
                    'success'
                )
            } catch (error) {
                console.error('Failed to reset project priorities:', error)
            }
        },
    },

    /**
     * Lifecycle hook called when the component is mounted.
     * Initializes the projects report data with the default period.
     */
    async mounted() {
        // Initialize with default period
        await this.updateProjectsReportData(this.selectedPeriod)
    },
}

